"use client"

import { FormDto, getUmbracoForm } from "@/api"
import { colors, CustomDialog, mediaQueries, sizes } from "@/ui"
import {
  sendForm,
  setHiddenFieldValues,
} from "@/ui/Component/FormBlock/service"
import FormBuilder from "@/ui/DynamicForm/FormBuilder"
import { ExternalApiFormStoreProvider } from "@/ui/DynamicForm/FormContext"
import { RelatedFieldsProvider } from "@/ui/DynamicForm/RelatedFieldsInFromContext"
import convertFormData from "@/ui/DynamicForm/utils/convertFormData"
import { submitCareerApplication } from "@/utils/careerApi"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { Box } from "@mui/system"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"
import { useJobsContext } from "../../JobsContext"

const DRUSHIM_FORM_ID = "797d9114-0d8c-4164-b3fa-b0ab097a508d"

const FormWrapper = styled(Box)`
  padding: 40px;
  padding-top: 70px;
  background-color: ${colors.beige};
  label.upload-link {
    display: flex;
    width: 100%;
    justify-content: center;
    background-color: white;
    text-align: center;
    padding: ${sizes.baseDouble};
    cursor: pointer;
  }
  input[type="file"] {
    cursor: pointer;
  }
  .upload-icon {
    display: none;
  }
  .form-small-bottom-text {
    text-align: start;
    margin-bottom: ${sizes.baseTriple};
  }
  .MuiButton-root {
    font-size: 16px;
  }
  .file-name {
    width: 100%;
  }
`

const FormTitleWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 32px;
  gap: 3.5px;
`

const MainTitle = styled(Typography)`
  color: ${colors.red};
  text-align: center;
  font-family: "Brown Trial Heb";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px; /* 162.5% */
`

const SubTitle = styled(Typography)`
  color: ${colors.black};
  text-align: center;
  font-family: "Brown Trial Heb";
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`

const StyledCustomDialog = styled(CustomDialog)`
  padding: 0;
  ${mediaQueries.tabletDown} {
    margin-left: 35px;
    margin-right: 35px;
    margin-top: 180px;
  }
  .MuiPaper-root {
    padding: 0px;
    height: auto;
    border-radius: 16px;
    width: auto;
  }
`

// Function to modify CV file field
const setCVFileFieldProperties = (formObject: FormDto) => {
  for (const page of formObject.pages) {
    for (const fieldset of page.fieldsets) {
      for (const column of fieldset.columns) {
        for (const field of column.fields) {
          if (field.alias === "cvFileBase64") {
            field.title = "אלה קורות החיים שלי"
            if (!field.helpText || field.helpText.trim() === "") {
              field.helpText = "צירוף קובץ בלחיצה או בגרירה"
            }
            return
          }
        }
      }
    }
  }
}

// Custom hook for form status
const useFormStatus = () => {
  const [status, setStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle")
  const setPending = () => setStatus("pending")
  const setSuccess = () => setStatus("success")
  const setError = () => setStatus("error")
  const reset = () => setStatus("idle")
  return { status, setPending, setSuccess, setError, reset }
}

type DrushimFormDialogProps = {
  open: boolean
  handleClose: () => void
  jobId: string
  jobTitle: string
  data: any
}
export const DrushimFormDialog = ({
  open,
  handleClose,
  jobId,
  jobTitle,
  data,
}: DrushimFormDialogProps) => {
  const t = useTranslations("DrushimFormDialog")
  const [formObject, setFormObject] = useState<FormDto | null>(null)
  const { status, setPending, setSuccess, setError, reset } = useFormStatus()
  const { setShowSuccessDialog, setShowFailedDialog } = useJobsContext()
  useEffect(() => {
    if (!open) {
      return
    }
    const initForm = async () => {
      if (!DRUSHIM_FORM_ID) {
        console.error("DRUSHIM_FORM_ID is not defined")
        return
      }
      const { data: formObject } = await getUmbracoForm(DRUSHIM_FORM_ID, "he")
      setHiddenFieldValues(formObject, "orderId", jobId)
      setCVFileFieldProperties(formObject)
      setFormObject(formObject)
      reset()
    }
    initForm()
  }, [open])

  const handleFormSubmit = async (formData: any) => {
    try {
      if (!formObject) {
        return
      }
      setPending()
      const convertedData = convertFormData(formData, formObject)

      // Submit career application using convertedData.values directly
      await submitCareerApplication(convertedData, jobId)
      // If submitCareerApplication was successful, remove cvFileBase64
      // before sending to Umbraco Forms
      if (convertedData.values && convertedData.values.cvFileBase64) {
        delete convertedData.values.cvFileBase64
      }
      // Also submit to Umbraco Forms for tracking/backup
      await sendForm(convertedData, formObject, "he")

      setSuccess()
      setShowSuccessDialog(true)
      handleClose()
    } catch (error) {
      setShowFailedDialog(true)
      // eslint-disable-next-line no-console
      console.error("Error submitting form", error)
      setError()
    }
  }

  if (!formObject) {
    return null
  }

  return (
    <StyledCustomDialog open={open} handleClose={handleClose}>
      <FormWrapper>
        <FormTitleWrapper>
          <MainTitle>{t("title")}</MainTitle>
          <SubTitle>{jobTitle}</SubTitle>
        </FormTitleWrapper>
        <ExternalApiFormStoreProvider>
          <RelatedFieldsProvider>
            <FormBuilder
              data={formObject}
              locale={"he"}
              onSubmit={handleFormSubmit}
            />
          </RelatedFieldsProvider>
        </ExternalApiFormStoreProvider>
      </FormWrapper>
    </StyledCustomDialog>
  )
}
